import api from "./api";
import { tokenService } from "./tokenService";
import {
  LoginCredentials,
  UserRegister,
  AuthResponse,
  User,
  TokenResponse,
  UserProfile,
  ChangePassword,
  AuthError,
  LoginResponse,
} from "../types/auth";
import axios from "axios";

export const setAuthHeader = (token: TokenResponse | null) => {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token.access}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

class AuthService {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const { email, password } = credentials;
      const response = await api.post<LoginResponse>("/api/auth/login/", {
        email,
        password,
      });
      return response.data;
    } catch (error: any) {
      const authError: AuthError = {
        message:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Erro ao fazer login",
        code: error.response?.status?.toString(),
        details: error.response?.data,
      };
      throw authError;
    }
  }

  async register(data: UserRegister): Promise<AuthResponse> {
    try {
      const response = await api.post<AuthResponse>(
        "/api/auth/register/",
        data
      );
      if (response.data.token) {
        tokenService.saveToken(response.data.token);
        setAuthHeader(response.data.token);
      }
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async logout(): Promise<void> {
    try {
      await api.post("/api/auth/logout/");
    } finally {
      tokenService.removeToken();
      setAuthHeader(null);
    }
  }

  async getProfile(): Promise<User> {
    try {
      const response = await api.get<User>("/api/auth/profile/");
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        tokenService.removeToken();
        setAuthHeader(null);
      }

      // Transformar erro em formato padronizado
      const authError: AuthError = {
        message:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Erro ao buscar perfil",
        code: error.response?.status?.toString(),
        details: error.response?.data,
      };
      throw authError;
    }
  }

  async updateProfile(data: Partial<UserProfile>): Promise<User> {
    try {
      const response = await api.patch<User>("/api/auth/profile/", data);
      return response.data;
    } catch (error: any) {
      // Log apenas em desenvolvimento
      if (process.env.NODE_ENV === "development") {
        console.error("Erro na API updateProfile:", error.response?.data);
      }
      throw error;
    }
  }

  async changePassword(data: ChangePassword): Promise<void> {
    await api.post("/api/auth/change-password/", data);
  }

  async sendResetPasswordLink(email: string): Promise<void> {
    await api.post("/api/auth/send-reset-password-link/", { login: email });
  }

  async setupPassword(data: {
    token: string;
    email: string;
    password: string;
  }): Promise<{ detail: string }> {
    const response = await api.post<{ detail: string }>(
      "/api/users/setup-password/",
      data
    );
    return response.data;
  }

  isAuthenticated(): boolean {
    const token = tokenService.getToken();
    if (token) {
      setAuthHeader(token);
      return true;
    }
    return false;
  }

  getToken(): TokenResponse | null {
    return tokenService.getToken();
  }
}

export const authService = new AuthService();
